import React, { forwardRef, useState, useMemo } from 'react';
import clsx from 'clsx';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Full name used for initials + color derivation */
  name: string;
  /** Avatar size */
  size?: 'sm' | 'md' | 'lg';
  /** Optional image URL */
  src?: string;
  /** Online status indicator */
  status?: 'online' | 'offline' | 'busy';
}

const sizeClasses: Record<NonNullable<AvatarProps['size']>, string> = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
};

const statusSizes: Record<NonNullable<AvatarProps['size']>, string> = {
  sm: 'h-2.5 w-2.5 border',
  md: 'h-3 w-3 border-2',
  lg: 'h-3.5 w-3.5 border-2',
};

const statusColors: Record<NonNullable<AvatarProps['status']>, string> = {
  online: 'bg-[var(--volt-success)]',
  offline: 'bg-[var(--volt-text-muted)]',
  busy: 'bg-[var(--volt-error)]',
};

/** VOLT accent palette for avatar backgrounds */
const avatarPalette = [
  'var(--volt-blue)',
  'var(--volt-fuchsia)',
  'var(--volt-cyan)',
  'var(--volt-success)',
  'var(--volt-warning)',
  'var(--volt-error)',
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ name, size = 'md', src, status, className, style, ...props }, ref) => {
    const [imgError, setImgError] = useState(false);
    const initials = useMemo(() => getInitials(name), [name]);
    const bgColor = useMemo(
      () => avatarPalette[hashString(name) % avatarPalette.length],
      [name],
    );

    const showImage = src && !imgError;

    return (
      <div
        ref={ref}
        className={clsx(
          'relative inline-flex flex-shrink-0 items-center justify-center rounded-full font-semibold select-none',
          sizeClasses[size],
          !showImage && 'text-white',
          className,
        )}
        style={{
          backgroundColor: showImage ? undefined : bgColor,
          ...style,
        }}
        aria-label={name}
        {...props}
      >
        {showImage ? (
          <img
            src={src}
            alt={name}
            className="h-full w-full rounded-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <span aria-hidden="true">{initials}</span>
        )}

        {status && (
          <span
            className={clsx(
              'absolute bottom-0 right-0 rounded-full',
              'border-[var(--volt-dark-surface)]',
              statusSizes[size],
              statusColors[status],
            )}
            aria-label={`Status: ${status}`}
          />
        )}
      </div>
    );
  },
);

Avatar.displayName = 'Avatar';
